import { Attendance } from '../types/attendance';
import axiosInstance from '../api/apiInterceptor';
import { AxiosResponse } from 'axios';
import {
  Attendance_API_ENDPOINT,
  ATTENDANE_API_ENDPOINT_STUDENT_MEAL,
  ATTENDANE_API_ENDPOINT_MEAL_DISTRIBUTION,
  MEAL_NOT_SERVED_REASONS,
} from "../../../constants/constants";

interface MealPayload {
  classSectionId?: number | null;
  mealDate: string;
  schoolId?: number;
  limit?: number;
}

interface MealDistributionPayload {
  classSectionId?: number | null;
  mealDate: string;
  schoolId?: number;
}

interface MealSubmissionPayload {
  classSectionId?: number;
  schoolId: number;
  mealDate: string;
  mealType: number;
  recordedVia: number;
  fileReference: {
    url: string;
  };
  mealServed: boolean;
  mealServedTime: string;
  mealServedBy: number;
  mealServedRemarks: string; 
  studentIds: number[];
  mealNotServedReasonId?: number; 
}

interface MealUpdatePayload {
  mealServedRemarks?: string;
  fileReference?: {
    url: string;
  };
  studentIds: number[];
  mealServedBy: number;
  mealServedTime: string;
  mealServed?: boolean; 
  mealNotServedReasonId?: number | undefined; 
}

// Extract error message helper
const extractErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error?.response?.data?.details) {
    return error.response.data.details;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Custom API Error class
class ApiError extends Error {
  public originalError: any;
  public statusCode?: number;
  
  constructor(message: string, originalError: any) {
    super(message);
    this.name = 'ApiError';
    this.originalError = originalError;
    this.statusCode = originalError?.response?.status;
  }
} 

// Fetch meal distributed students
export const fetchMealsApi = async (payload: MealPayload): Promise<any[]> => {
  try {
    let url = ATTENDANE_API_ENDPOINT_STUDENT_MEAL;
    
    const filterParams = []; 
    
    if (payload.schoolId) {
      filterParams.push(`schoolId|${payload.schoolId}`);
    }
    
    if (payload.classSectionId) {
      filterParams.push(`classSectionId|${payload.classSectionId}`);
    }
    
    if (payload.mealDate) {
      filterParams.push(`mealDate|${payload.mealDate}`);
    }
    
    if (filterParams.length > 0) {
      url += `?filter=${filterParams.join(',')}`;
    }
    
    if (payload.limit) {
      const separator = filterParams.length > 0 ? '&' : '?';
      url += `${separator}limit=${payload.limit}`;
    }
    
    const response: AxiosResponse<any> = await axiosInstance.get(url);
    return response.data.data || response.data || [];
  } catch (error: any) {
    console.error('Error fetching Meals:', error);
    if (error?.response?.status === 404 || 
        error?.response?.status === 400 ||
        error?.message?.toLowerCase().includes('not found')) {
      return [];
    }
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchMealDistributionApi = async (payload: MealDistributionPayload): Promise<any[]> => {
  try {
    let url = ATTENDANE_API_ENDPOINT_MEAL_DISTRIBUTION;
    
    const filterParams = [];
    
    if (payload.schoolId) {
      filterParams.push(`schoolId|${payload.schoolId}`);
    }
    
    if (payload.classSectionId) {
      filterParams.push(`classSectionId|${payload.classSectionId}`);
    }
    
    if (payload.mealDate) {
      filterParams.push(`mealDate|${payload.mealDate}`);
    }
    
    if (filterParams.length > 0) {
      url += `?filter=${filterParams.join(',')}`;
    }
    
    const response: AxiosResponse<any> = await axiosInstance.get(url);
    return response.data.data || response.data || [];
  } catch (error: any) {
    console.error('Error fetching meal distribution:', error);
    if (error?.response?.status === 404 || 
        error?.response?.status === 400 ||
        error?.message?.toLowerCase().includes('not found')) {
      return [];
    }
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const checkMealSubmissionExistsApi = async (payload: MealDistributionPayload): Promise<{exists: boolean, data: any, canEdit: boolean}> => {
  try {
    const mealDistribution = await fetchMealDistributionApi(payload);
    const exists = Array.isArray(mealDistribution) && mealDistribution.length > 0;
    const data = exists ? mealDistribution[0] : null;
    const canEdit = exists && data && (data.editedCount === 0 || data.editedCount === null);
    
    return { exists, data, canEdit };
  } catch (error: any) {
    console.error('Error checking meal submission existence:', error);
    return { exists: false, data: null, canEdit: false };
  }
};

export const addAttendanceApi = async (AttendanceData: Omit<Attendance, 'id'>): Promise<Attendance> => {
  try {
    const response: AxiosResponse<Attendance> = await axiosInstance.post(Attendance_API_ENDPOINT, AttendanceData);
    return response.data;
  } catch (error: any) {
    console.error('Error adding Attendance:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};
 
export const updateAttendanceApi = async (Attendance: Attendance): Promise<Attendance> => {
  try {
    const response: AxiosResponse<Attendance> = await axiosInstance.patch(`${Attendance_API_ENDPOINT}/${Attendance.id}`, Attendance);
    return response.data;
  } catch (error: any) {
    console.error('Error updating Attendance:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const deleteAttendanceApi = async (id: number): Promise<number> => {
  try {
    await axiosInstance.delete(`${Attendance_API_ENDPOINT}/${id}`);
    return id;
  } catch (error: any) {
    console.error('Error deleting Attendance:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const setMealApi = async (MealData: MealSubmissionPayload): Promise<any> => {
  try {
    const url = 'meal-distributed-students';
    
    const payload: any = {
      ...MealData,
      fileReference: typeof MealData.fileReference === 'object' 
        ? MealData.fileReference 
        : { url: MealData.fileReference || "https://example.com/default-file.jpg" },
      studentIds: Array.isArray(MealData.studentIds) ? MealData.studentIds : [],
    };
    if (MealData.mealNotServedReasonId) {
      payload.mealNotServedReasonId = MealData.mealNotServedReasonId;
    }

    console.log('Meal API payload being sent:', JSON.stringify(payload, null, 2));

    const response: AxiosResponse<any> = await axiosInstance.post(url, payload);
    return response.data;
  } catch (error: any) {
    console.error('Error submitting meals:', error);
         
    if (error.response?.data?.message) {
      throw new ApiError(error.response.data.message, error);
    } else if (error.message) {
      throw new ApiError(error.message, error);
    } else {
      throw new ApiError('Failed to submit meals', error);
    }
  }
};

export const updateMealApi = async (mealId: number, MealData: MealUpdatePayload): Promise<any> => {
  try {
    const url = `meal-distributed-students/${mealId}`;
    const payload: any = {
      mealServedRemarks: MealData.mealServedRemarks,
      fileReference: typeof MealData.fileReference === 'object' 
        ? MealData.fileReference 
        : { url: MealData.fileReference || "https://example.com/default-file.jpg" },
      studentIds: Array.isArray(MealData.studentIds) ? MealData.studentIds : [],
      mealServedBy: MealData.mealServedBy,
      mealServedTime: MealData.mealServedTime,
    };

    if (MealData.hasOwnProperty('mealNotServedReasonId')) {
      payload.mealNotServedReasonId = MealData.mealNotServedReasonId === undefined ? null : MealData.mealNotServedReasonId;
    }

    if (MealData.hasOwnProperty('mealServed')) {
      payload.mealServed = MealData.mealServed;
    }

    console.log('Meal update API payload being sent:', JSON.stringify(payload, null, 2));

    const response: AxiosResponse<any> = await axiosInstance.patch(url, payload);
    return response.data;
  } catch (error: any) {
    console.error('Error updating meals:', error);
         
    if (error.response?.data?.message) {
      throw new ApiError(error.response.data.message, error);
    } else if (error.message) {
      throw new ApiError(error.message, error);
    } else {
      throw new ApiError('Failed to update meals', error);
    }
  }
};

// FIXED: Get meal not served reasons from API
export const mealReasonApi = async (): Promise<any[]> => {
  try {
    const url = MEAL_NOT_SERVED_REASONS;
    const response: AxiosResponse<any> = await axiosInstance.get(url);
    return response.data.data || response.data || [];
  } catch (error: any) {
    console.error('Error fetching meal reasons:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// ADDITIONAL: Helper function to format meal distribution data for display
export const formatMealDistributionData = (mealData: any) => {
  if (!mealData) return null;

  return {
    id: mealData.id,
    classSectionId: mealData.classSectionId,
    schoolId: mealData.schoolId,
    mealDate: mealData.mealDate,
    mealServed: mealData.mealServed,
    mealServedCount: mealData.mealServedCount || 0,
    mealServedRemarks: mealData.mealServedRemarks || '',
    mealNotServedReasonId: mealData.mealNotServedReasonId || undefined,
    notServedReason: mealData.notServedReason || null,
    editedCount: mealData.editedCount || 0,
    canEdit: (mealData.editedCount === 0 || mealData.editedCount === null),
    fileReference: mealData.fileReference || { url: '' },
    createdAt: mealData.createdAt,
    updatedAt: mealData.updatedAt,
    createdBy: mealData.createdBy,
    mealServedBy: mealData.mealServedBy,
    mealServedTime: mealData.mealServedTime
  };
};

// ADDITIONAL: Helper function to validate meal submission payload
export const validateMealSubmissionPayload = (payload: MealSubmissionPayload): { isValid: boolean, errors: string[] } => {
  const errors: string[] = [];

  if (!payload.schoolId) {
    errors.push('School ID is required');
  }

  if (!payload.mealDate) {
    errors.push('Meal date is required');
  }

  if (!payload.mealServedBy) {
    errors.push('Meal served by (user ID) is required');
  }

  if (payload.mealServed && (!payload.studentIds || payload.studentIds.length === 0)) {
    errors.push('Student IDs are required when meals are served');
  }
  if (!payload.mealServed && !payload.mealNotServedReasonId) {
    errors.push('Meal not served reason is required when meals are not served');
  }

  if (!payload.classSectionId && payload.mealServed) {
    console.warn('Class section ID not provided - this may be a school-wide submission');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const handleMealApiError = (error: any, operation: string = 'meal operation') => {
  console.error(`Error in ${operation}:`, error);
  
  if (error instanceof ApiError) {
    return error.message;
  }
  
  const message = extractErrorMessage(error);
  return `Failed to complete ${operation}: ${message}`;
};